import React, {
  useEffect, useId, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Loader2, X } from 'lucide-react';

const ConfirmDialog = ({
  open,
  title,
  description,
  // Açıklamanın altına eklenen serbest içerik (ör. ek bir seçim kutusu).
  extra = null,
  confirmLabel = 'Onayla',
  cancelLabel = 'Vazgeç',
  tone = 'danger',
  busy = false,
  onCancel,
  onConfirm,
  // Geri alınamayan işlemlerde (yedeği geri yükleme) onay düğmesi ancak bu
  // ifade kutuya yazıldığında açılır; tek tıkla yanlışlıkla onay verilmez.
  confirmPhrase = '',
}) => {
  const titleId = useId();
  const phraseId = useId();
  const [typed, setTyped] = useState('');
  const norm = (value) => value.trim().toLocaleUpperCase('tr');
  const phraseOk = !confirmPhrase || norm(typed) === norm(confirmPhrase);

  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);
  const descriptionId = useId();
  const cancelRef = useRef(null);
  const dialogRef = useRef(null);

  // Odak YALNIZCA pencere açıldığında verilir. Bağımlılığa `busy`/`onCancel`
  // eklenirse effect her render'da yeniden çalışır: çağıran taraf bu iki
  // prop'u satır içi arrow fonksiyonla verdiği için `extra` alanına yazılan
  // her harf odağı kutudan çalıp Vazgeç düğmesine taşırdı.
  useEffect(() => {
    if (!open) return;
    // Yalnızca metin alanı odak alır: yıkıcı onaylarda `extra` bir onay kutusu
    // olabiliyor (Users.jsx "geçmişini de kalıcı sil") ve açılışta ona odaklanmak
    // Space/Enter ile istemeden işaretlenmesine yol açardı — orada odak Vazgeç'te
    // kalmalıdır.
    const field = dialogRef.current?.querySelector(
      'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), textarea, select',
    );
    (field || cancelRef.current)?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !busy) onCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [busy, onCancel, open]);

  if (!open) return null;

  return createPortal((
    <div className="admin-dialog-layer admin-portal-theme" role="presentation">
      <button
        type="button"
        className="admin-dialog-backdrop"
        aria-label="Onay penceresini kapat"
        onClick={() => !busy && onCancel()}
      />
      <section
        ref={dialogRef}
        className="admin-dialog admin-card"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        aria-busy={busy}
      >
        <div className={`admin-dialog-icon ${tone}`}>
          <AlertTriangle size={20} aria-hidden="true" />
        </div>
        <button
          type="button"
          className="icon-btn view admin-dialog-close"
          onClick={onCancel}
          disabled={busy}
          aria-label="Pencereyi kapat"
        >
          <X size={15} />
        </button>
        <h2 id={titleId}>{title}</h2>
        <p id={descriptionId}>{description}</p>
        {extra}
        {confirmPhrase && (
          <div className="admin-dialog-phrase">
            <label htmlFor={phraseId}>
              Onaylamak için kutuya <b>{confirmPhrase}</b> yazın
            </label>
            <input
              id={phraseId}
              className="admin-input"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              autoComplete="off"
              spellCheck={false}
              disabled={busy}
            />
          </div>
        )}
        <div className="admin-dialog-actions">
          <button
            ref={cancelRef}
            type="button"
            className="admin-btn-secondary"
            onClick={onCancel}
            disabled={busy}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={tone === 'danger' ? 'admin-btn-danger' : 'admin-btn-primary'}
            onClick={onConfirm}
            disabled={busy || !phraseOk}
          >
            {busy && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
            {confirmLabel}
          </button>
        </div>
      </section>
    </div>
  ), document.body);
};

export default ConfirmDialog;
